import React, { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import dayjs from 'dayjs';
import AutocompleteSelectField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/AutocompleteSelectField';
import DatePickerField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/DatePickerField';
import NumberInputField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/NumberInputField';
import CheckboxField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/CheckboxField';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import ClassesStudentsSelectDropdown from './ClassesStudentsSelectDropdown';
import { FIELD_META_MAP } from 'chameleon/src/components/assign/AssignManageForm/utils/constants';

import { useStartDueDateFields } from './hooks';

export const FIELD_NAMES = {
  STARTS_AT: 'starts_at',
  DUE_AT: 'due_at',
  FORMAT: 'print_toggle',
  TIMER: 'timer',
  STUDENT_RESULTS: 'score_display',
  CLASS_DROPDOWN: 'classesStudents',
  LATE_SUBMISSION: 'allow_late_submission',
  IS_SOFT_TIMER: 'is_soft_timer',
};
const BasicSettings = ({
  id,
  scrambleQuestionsPerStudent,
  reviewModeFlag,
  isPreApSubject,
  isSPP,
  isMCQPPC,
  individualStudentAssignmentFlag,
  allowLateSubmissionFF,
  assignments,
  studentsBySection,
  router,
  isAssessmentForm,
  timezone,
  assignableSections,
  disabledStudents,
  casSettingsCount,
  otherActiveSettings,
  isAdminOrCoordinator,
  allowHardTimer,
  isLC = false,
}) => {
  const { setValue, getValues, watch, formState } = useFormContext();
  const dueAt = watch(`${id}.${FIELD_NAMES.DUE_AT}`);
  const timer = watch(`${id}.${FIELD_NAMES.TIMER}`);
  const isPrintFormat = watch(`${id}.${FIELD_NAMES.FORMAT}`);

  const {
    startDateMaxDate,
    startDateMinDate,
    formatedStartsAt,
    dueAtMinDate,
    formatedDueAt,
  } = useStartDueDateFields({
    id,
    timezone,
    startsAtName: FIELD_NAMES.STARTS_AT,
    dueAtName: FIELD_NAMES.DUE_AT,
  });
  const errors = get(formState.errors, id, {});

  const isDateVisible = !isPrintFormat;
  const isTimerVisible = !isPrintFormat && !isSPP;
  const isFormatVisible = !isSPP;
  const isStudentSeeResultsVisible = !isSPP;

  const assignmentInfo = {
    isPreApSubject,
    isResourceForm: false,
    isSPP,
    isAssign: true,
    hasPreassignedBehavior: false,
    individualStudentAssignmentFlag,
    isMCQPPC,
    isLC,
    featureFlags: {
      scrambleQuestionsPerStudent,
      reviewModeFlag,
      allowLateSubmission: allowLateSubmissionFF,
    },
  };

  const handleValueChange = (field, value) => {
    setValue(`${id}.${field}`, value, { shouldValidate: true });
  };

  let settingNumber = '';
  if (casSettingsCount > 0) {
    settingNumber = ' 1';
  }

  const disablePartialResults = isPreApSubject;

  return (
    <div>
      <div
        className="mb-6"
        id={`${id}-heading`}
      >{`Assignment Settings${settingNumber}`}</div>
      <div className="flex gap-x-6">
        <div className="flex-1">
          <ClassesStudentsSelectDropdown
            classes={assignableSections || []}
            data-test-id={`${id}-sections`}
            title={FIELD_META_MAP.SECTIONS.title(assignmentInfo)}
            infoPopupText={FIELD_META_MAP.SECTIONS.helpText(assignmentInfo)}
            selectedValue={getValues(FIELD_NAMES.CLASS_DROPDOWN)}
            onValueChange={(value) =>
              handleValueChange(FIELD_NAMES.CLASS_DROPDOWN, value)
            }
            allAssigneesName="allAssignees"
            id={id}
            disabledStudents={disabledStudents}
            inProgressAssignments={assignments}
            studentsBySection={studentsBySection}
            router={router}
            isAssessmentForm={isAssessmentForm}
            otherActiveSettings={otherActiveSettings}
            isPlaceholder={isAdminOrCoordinator}
          />
          {isFormatVisible && (
            <AutocompleteSelectField
              disableDropdown={isMCQPPC || isLC}
              data-test-id={`${id}-${FIELD_NAMES.FORMAT}`}
              name={`${id}.${FIELD_NAMES.FORMAT}`}
              title={FIELD_META_MAP.FORMAT.title()}
              infoPopupText={FIELD_META_MAP.FORMAT.helpText({
                isMCQPPC,
                featureFlags: { scrambleQuestionsPerStudent },
                isAssign: true,
              })}
              options={FIELD_META_MAP.FORMAT.options}
              placeholder={FIELD_META_MAP.FORMAT.placeholder}
              onValueChange={(value) =>
                handleValueChange(FIELD_NAMES.FORMAT, value)
              }
            />
          )}
          {isStudentSeeResultsVisible && (
            <AutocompleteSelectField
              data-test-id={`${id}-${FIELD_NAMES.STUDENT_RESULTS}`}
              name={`${id}.${FIELD_NAMES.STUDENT_RESULTS}`}
              title={FIELD_META_MAP.STUDENT_RESULTS.title()}
              infoPopupText={FIELD_META_MAP.STUDENT_RESULTS.helpText({
                featureFlags: { reviewModeFlag },
              })}
              options={FIELD_META_MAP.STUDENT_RESULTS.options(
                reviewModeFlag,
                disablePartialResults,
              )}
              placeholder={FIELD_META_MAP.STUDENT_RESULTS.placeholder}
              onValueChange={(value) =>
                handleValueChange(FIELD_NAMES.STUDENT_RESULTS, value)
              }
            />
          )}
        </div>
        <div className="flex-1">
          {isDateVisible && (
            <>
              <DatePickerField
                name={`${id}.${FIELD_NAMES.STARTS_AT}`}
                data-test-id={`${id}-${FIELD_NAMES.STARTS_AT}`}
                title={FIELD_META_MAP.START_DATE.title(assignmentInfo)}
                infoPopupText={FIELD_META_MAP.START_DATE.helpText(
                  assignmentInfo,
                )}
                error={
                  errors[FIELD_NAMES.STARTS_AT] && {
                    message: errors[FIELD_NAMES.STARTS_AT].message,
                  }
                }
                onReset={() => handleValueChange(FIELD_NAMES.STARTS_AT, null)}
                maxDate={startDateMaxDate}
                minDate={startDateMinDate}
                initialValue={formatedStartsAt}
                timezone={timezone}
              />
              <DatePickerField
                name={`${id}.${FIELD_NAMES.DUE_AT}`}
                data-test-id={`${id}-${FIELD_NAMES.DUE_AT}`}
                title={FIELD_META_MAP.DUE_DATE.title(assignmentInfo)}
                infoPopupText={FIELD_META_MAP.DUE_DATE.helpText(assignmentInfo)}
                error={
                  errors[FIELD_NAMES.DUE_AT] && {
                    message: errors[FIELD_NAMES.DUE_AT].message,
                  }
                }
                onReset={() => handleValueChange(FIELD_NAMES.DUE_AT, null)}
                minDate={dueAtMinDate}
                initialValue={formatedDueAt}
                timezone={timezone}
              >
                {dueAt && dayjs(dueAt).isValid() && (
                  <>
                    <div
                      className="mb-4 text-base leading-relaxed text-gray-600"
                      role="region"
                      aria-live="polite"
                    >
                      {FIELD_META_MAP.DUE_DATE.detailsComponent({
                        allowLateSubmission: allowLateSubmissionFF,
                      })}
                    </div>
                    {allowLateSubmissionFF && (
                      <CheckboxField
                        name={`${id}.${FIELD_NAMES.LATE_SUBMISSION}`}
                        data-test-id={`${id}.${FIELD_NAMES.LATE_SUBMISSION}`}
                        title="Allow late submissions"
                      />
                    )}
                  </>
                )}
              </DatePickerField>
            </>
          )}
          {isTimerVisible && (
            <NumberInputField
              name={`${id}.${FIELD_NAMES.TIMER}`}
              data-test-id={`${id}-${FIELD_NAMES.TIMER}`}
              title={FIELD_META_MAP.TIMER.title()}
              infoPopupText={FIELD_META_MAP.TIMER.helpText({ allowHardTimer })}
              icon={<Icon id="timer" accessibility={{ ariaHidden: true }} />}
              description={timer > 1 ? 'mins' : 'min'}
              onValueChange={(value) =>
                handleValueChange(FIELD_NAMES.TIMER, value)
              }
            >
              {!!timer && (
                <Fragment>
                  <div
                    className="text-base leading-relaxed text-gray-600 mb-4"
                    role="region"
                    aria-live="polite"
                    data-test-id={`${id}-timer-details`}
                  >
                    {FIELD_META_MAP.TIMER.detailsComponent({
                      allowHardTimer,
                    })}
                  </div>
                  {allowHardTimer && (
                    <CheckboxField
                      name={`${id}.${FIELD_NAMES.IS_SOFT_TIMER}`}
                      data-test-id={`${id}-${FIELD_NAMES.IS_SOFT_TIMER}`}
                      title="Allow submissions after timer expires"
                    />
                  )}
                </Fragment>
              )}
            </NumberInputField>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicSettings;
